var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Autoplay, Navigation, Pagination, Thumbs, Zoom } from 'swiper/modules';
import styles from './ResponsiveSlider.module.scss';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';
const clamp = (val, min, max) => {
    if (typeof max === 'number')
        return Math.max(min, Math.min(max, val));
    return Math.max(min, val);
};
export function ResponsiveSlider(props) {
    var _a;
    const { items, renderItem, itemKey, itemMinWidth = 280, getItemMinWidth, minSlidesPerView = 1, maxSlidesPerView, slideHeight, aspectRatio, onIndexChange, containerClassName = '', ariaLabel = 'Content slider', onSwiper, 
    // Pull some Swiper props we care about for defaults/logic; rest will be forwarded
    slidesPerView: slidesPerViewProp, spaceBetween = 16, direction = 'horizontal', navigation, pagination, autoplay, loop, freeMode, watchSlidesProgress, breakpoints, zoom, thumbs, className, modules: providedModules, onInit: providedOnInit, onSlideChange: providedOnSlideChange, a11y: providedA11y, a11yMessages } = props, restSwiperProps = __rest(props, ["items", "renderItem", "itemKey", "itemMinWidth", "getItemMinWidth", "minSlidesPerView", "maxSlidesPerView", "slideHeight", "aspectRatio", "onIndexChange", "containerClassName", "ariaLabel", "onSwiper", "slidesPerView", "spaceBetween", "direction", "navigation", "pagination", "autoplay", "loop", "freeMode", "watchSlidesProgress", "breakpoints", "zoom", "thumbs", "className", "modules", "onInit", "onSlideChange", "a11y", "a11yMessages"]);
    const rootRef = useRef(null);
    const swiperRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const resizeTimer = useRef(null);
    useEffect(() => {
        if (!rootRef.current)
            return;
        const el = rootRef.current;
        const updateSize = () => {
            if (resizeTimer.current)
                window.clearTimeout(resizeTimer.current);
            resizeTimer.current = window.setTimeout(() => {
                setContainerWidth(el.clientWidth);
                setContainerHeight(el.clientHeight);
            }, 60);
        };
        updateSize();
        let ro = null;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => updateSize());
            ro.observe(el);
        }
        else {
            window.addEventListener('resize', updateSize);
        }
        return () => {
            if (ro)
                ro.disconnect();
            else
                window.removeEventListener('resize', updateSize);
        };
    }, []);
    // Normalize spaceBetween to a number for internal layout math
    const spaceBetweenPx = typeof spaceBetween === 'number' ? spaceBetween : parseFloat(String(spaceBetween)) || 0;
    const computedSlidesPerView = useMemo(() => {
        if (typeof slidesPerViewProp !== 'undefined')
            return slidesPerViewProp;
        // Use main-axis size: width for horizontal, height for vertical
        const mainAxisSize = direction === 'vertical' ? containerHeight : containerWidth;
        const resolvedMinSize = getItemMinWidth ? getItemMinWidth(mainAxisSize) : itemMinWidth;
        if (!mainAxisSize || !resolvedMinSize)
            return minSlidesPerView;
        const totalUnit = resolvedMinSize + spaceBetweenPx;
        const raw = Math.floor((mainAxisSize + spaceBetweenPx) / Math.max(1, totalUnit));
        return clamp(raw, minSlidesPerView, maxSlidesPerView);
    }, [
        slidesPerViewProp,
        containerWidth,
        containerHeight,
        direction,
        itemMinWidth,
        getItemMinWidth,
        spaceBetweenPx,
        minSlidesPerView,
        maxSlidesPerView,
    ]);
    // navigation/pagination derived from either native props or convenience flags
    const navProp = navigation !== undefined ? navigation : (_a = props.showNavigation) !== null && _a !== void 0 ? _a : true;
    const pagProp = pagination !== undefined ? (pagination === true ? { clickable: true } : pagination) : props.showPagination ? { clickable: true } : false;
    // Register Swiper modules based on props
    const modules = [...(providedModules !== null && providedModules !== void 0 ? providedModules : []), A11y];
    if (navProp)
        modules.push(Navigation);
    if (pagProp)
        modules.push(Pagination);
    if (autoplay)
        modules.push(Autoplay);
    if (zoom)
        modules.push(Zoom);
    if (thumbs)
        modules.push(Thumbs);
    const handleInit = useCallback((swiper) => {
        var _a;
        swiperRef.current = swiper;
        (_a = props.onSwiper) === null || _a === void 0 ? void 0 : _a.call(props, swiper);
        providedOnInit === null || providedOnInit === void 0 ? void 0 : providedOnInit(swiper);
    }, [props, providedOnInit]);
    const handleSlideChange = useCallback((swiper) => {
        var _a, _b;
        const index = (_b = (_a = swiper.realIndex) !== null && _a !== void 0 ? _a : swiper.activeIndex) !== null && _b !== void 0 ? _b : 0;
        onIndexChange === null || onIndexChange === void 0 ? void 0 : onIndexChange(index);
        providedOnSlideChange === null || providedOnSlideChange === void 0 ? void 0 : providedOnSlideChange(swiper);
    }, [onIndexChange, providedOnSlideChange]);
    useEffect(() => {
        var _a, _b;
        (_b = (_a = swiperRef.current) === null || _a === void 0 ? void 0 : _a.update) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, [computedSlidesPerView, spaceBetweenPx]);
    useEffect(() => {
        const el = rootRef.current;
        if (!el)
            return;
        if (aspectRatio && containerWidth) {
            const computed = `${Math.max(1, Math.floor(containerWidth / aspectRatio))}px`;
            el.style.setProperty('--responsive-slide-height', computed);
        }
        else if (typeof slideHeight !== 'undefined') {
            const value = typeof slideHeight === 'number' ? `${slideHeight}px` : `${slideHeight}`;
            el.style.setProperty('--responsive-slide-height', value);
        }
        else {
            el.style.removeProperty('--responsive-slide-height');
        }
    }, [aspectRatio, slideHeight, containerWidth]);
    if (!items || items.length === 0) {
        return (_jsx("div", Object.assign({ ref: rootRef, className: `${styles['responsive-slider']} ${containerClassName}`, role: 'region', "aria-label": ariaLabel }, { children: _jsx("div", Object.assign({ className: styles.empty }, { children: "No content" })) })));
    }
    // Build Swiper props object cleanly for TS
    const autoplayOption = autoplay === true ? { delay: 3000 } : typeof autoplay === 'object' ? autoplay : undefined;
    // Build A11y config, merging optional message overrides while preserving base options
    let a11yConfig = providedA11y;
    const hasA11yMessages = !!a11yMessages && Object.keys(a11yMessages).length > 0;
    if (hasA11yMessages || providedA11y) {
        const baseOptions = (typeof providedA11y === 'object' ? providedA11y : {});
        a11yConfig = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, baseOptions), (baseOptions.enabled === undefined ? { enabled: true } : {})), ((a11yMessages === null || a11yMessages === void 0 ? void 0 : a11yMessages.prevSlideMessage) ? { prevSlideMessage: a11yMessages.prevSlideMessage } : {})), ((a11yMessages === null || a11yMessages === void 0 ? void 0 : a11yMessages.nextSlideMessage) ? { nextSlideMessage: a11yMessages.nextSlideMessage } : {})), ((a11yMessages === null || a11yMessages === void 0 ? void 0 : a11yMessages.paginationBulletMessage) ? { paginationBulletMessage: a11yMessages.paginationBulletMessage } : {})), ((a11yMessages === null || a11yMessages === void 0 ? void 0 : a11yMessages.firstSlideMessage) ? { firstSlideMessage: a11yMessages.firstSlideMessage } : {})), ((a11yMessages === null || a11yMessages === void 0 ? void 0 : a11yMessages.lastSlideMessage) ? { lastSlideMessage: a11yMessages.lastSlideMessage } : {}));
    }
    const swiperProps = Object.assign(Object.assign({}, restSwiperProps), { modules, onInit: handleInit, onSlideChange: handleSlideChange, slidesPerView: computedSlidesPerView, spaceBetween: spaceBetweenPx, direction, navigation: navProp, pagination: pagProp, autoplay: autoplayOption, loop,
        freeMode,
        watchSlidesProgress, observer: true, observeParents: true, className: [styles.swiper, className].filter(Boolean).join(' '), breakpoints,
        zoom,
        thumbs, a11y: a11yConfig });
    return (_jsx("div", Object.assign({ ref: rootRef, className: `${styles['responsive-slider']} ${containerClassName}`, role: 'region', "aria-label": ariaLabel }, { children: _jsx(Swiper, Object.assign({}, swiperProps, { children: items.map((item, index) => {
                var _a;
                return (_jsx(SwiperSlide, Object.assign({ className: styles.slide }, { children: renderItem(item, index) }), (_a = itemKey === null || itemKey === void 0 ? void 0 : itemKey(item, index)) !== null && _a !== void 0 ? _a : index));
            }) })) })));
}
export default ResponsiveSlider;
